<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/interactions';
import typesDocs from 'docs:@react-types/shared/src/events.d.ts';
import {HeaderInfo, FunctionAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-aria/interactions/package.json';

```jsx import
import {useFocus} from '@react-aria/interactions';
```

---
category: Interactions
keywords: [keyboard focus, focus management, focus pseudo class, aria]
---

# useFocus

<p>{docs.exports.useFocus.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useFocus']} />

## API

<FunctionAPI function={docs.exports.useFocus} links={docs.links} />

## Features

`useFocus` handles focus interactions for an element. Unlike React's built-in focus events,
`useFocus` does not fire focus events for child elements of the target. This matches DOM
behavior where focus events do not bubble. This is similar to
the [:focus](https://developer.mozilla.org/en-US/docs/Web/CSS/:focus) pseudo class
in CSS.

To handle focus events on descendants of an element, see [useFocusWithin](useFocusWithin.html).

## Usage

`useFocus` returns props that you should spread onto the target element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useFocus.return.id].properties} />
</TypeContext.Provider>

`useFocus` supports the following event handlers:

<TypeContext.Provider value={typesDocs.links}>
  <InterfaceType properties={typesDocs.exports.FocusEvents.properties} />
</TypeContext.Provider>

## Example

This example shows a simple input element that handles focus events with `useFocus` and logs them to a list below.

**NOTE: for more advanced text field functionality, see [useTextField](useTextField.html).**

```tsx example
function Example() {
  let [events, setEvents] = React.useState([]);
  let {focusProps} = useFocus({
    onFocus: e => setEvents(
      events => [...events, 'focus']
    ),
    onBlur: e => setEvents(
      events => [...events, 'blur']
    ),
    onFocusChange: isFocused => setEvents(
      events => [...events, `focus change: ${isFocused}`]
    )
  });

  return (
    <>
      <input
        {...focusProps}
        aria-label="Example"
        placeholder="Focus me!" />
      <ul
        style={{
          maxHeight: '200px',
          overflow: 'auto'
        }}>
        {events.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </>
  );
}
```
